import { adSubjectFromItem, promotionOfferTitle, subjectLandingUrl, type GameLogos } from './ad-subjects';
import type { AdBrand } from './banner-kit-api';
import type { CreativeItem } from './creative-api';
import { miniGameSubject, withLockedGameText } from './mini-game-copy';
import type { PlayableParams } from './playable-api';

/**
 * A mini-game always advertises the brand's welcome bonus. These pure helpers pick that promotion out of the brand's
 * promotion pool when a mini-game is created, and swap it for another promotion when the owner changes it in the
 * composer.
 */

const WELCOME_RE = /welcome/i;
const DEPOSIT_RE = /deposit|paymentType=deposit|rewardId=/i;

/**
 * The brand's welcome bonus among its promotions, or undefined when it has none:
 * 1. the promotion with the highest `priority` above zero (the first of equals);
 * 2. else the first whose headline or internal name says "welcome";
 * 3. else the first whose link goes to a deposit or claims a reward;
 * 4. else the first promotion.
 * Pure.
 */
export const detectWelcomeBonus = (items: readonly CreativeItem[]): CreativeItem | undefined => {
  let best: CreativeItem | undefined;
  for (const item of items) {
    const p = item.priority ?? 0;
    if (p > 0 && (!best || p > (best.priority ?? 0))) best = item;
  }
  if (best) return best;
  return (
    items.find((i) => WELCOME_RE.test(i.headline ?? '') || WELCOME_RE.test(i.internal_name ?? '')) ??
    items.find((i) => DEPOSIT_RE.test(i.cta_href ?? '')) ??
    items[0]
  );
};

/** The promotion's display name (its headline, else internal name, else id). Pure. */
export const promotionName = (item: Pick<CreativeItem, 'headline' | 'internal_name' | 'banner_id'>): string =>
  item.headline || item.internal_name || item.banner_id;

/**
 * The mini-game document with its offer locked to the promotion's own (an offer the owner cannot edit, so the game
 * never promises more than the live promotion does): the offer title is the promotion's full offer
 * ({@link promotionOfferTitle}) in the ad's language (the promotion's copy in it, `miniGameSubject`; as snapshotted on
 * an older document with no language), and there are no significant terms, since a promotion carries none to take
 * them from. The T&Cs link stays the owner's. Idempotent. Pure.
 */
export const withLockedOffer = (doc: PlayableParams): PlayableParams => ({
  ...doc,
  offer: { ...doc.offer, title: promotionOfferTitle(miniGameSubject(doc)), terms: '' },
});

/**
 * The mini-game document with everything it says locked: the offer ({@link withLockedOffer}), the game's fixed text
 * with the promotion's call to action and the preset legal line (`withLockedGameText`). An owner writes none of the
 * words a mini-game shows. Applied wherever the document is used (previews, the run, the package) and when the game
 * or the promotion changes; never patched into a reopened document. Idempotent. Pure.
 */
export const withLockedMiniGame = (doc: PlayableParams): PlayableParams => withLockedOffer(withLockedGameText(doc));

/**
 * The mini-game document drawn with the brand's logos as they are now ({@link GameLogos}: the primary logo, and the bare
 * symbol as `brand.logos.symbol`). A mini-game's logo is the brand's, never the owner's choice, so like its locked
 * words it is applied wherever the document is used (previews, the run) and never patched in on open: a game made
 * with the white wordmark shows the primary logo without forking. Unchanged while the logos are unknown. Idempotent.
 * Pure.
 */
export const withGameLogos = (doc: PlayableParams, logos: GameLogos | undefined): PlayableParams => {
  if (!logos?.logo_url) return doc;
  const brand: AdBrand = { ...doc.brand, logo_url: logos.logo_url };
  if (logos.symbol_url) brand.logos = { symbol: logos.symbol_url };
  else delete brand.logos;
  return { ...doc, brand };
};

/**
 * The mini-game document advertising another promotion, with its words locked to it ({@link withLockedMiniGame}: the
 * new promotion's offer and call to action, the game text in the ad's language; the new promotion's copy in that
 * language follows when it arrives, `localized_copy` naming the subject it is for). The landing page follows the new
 * promotion only while it still holds the old one's (or is empty), so the owner's own landing page survives the swap.
 * Pure.
 */
export const withPromotionSubject = (doc: PlayableParams, item: CreativeItem, brandDomain?: string): PlayableParams => {
  const prev = doc.subject;
  const next = adSubjectFromItem(item);
  const follows = (current: string, previous: string): boolean => !current.trim() || current.trim() === previous.trim();
  return withLockedMiniGame({
    ...doc,
    subject_type: 'promotion',
    subject: next,
    landing_url: follows(doc.landing_url, subjectLandingUrl(prev, brandDomain))
      ? subjectLandingUrl(next, brandDomain)
      : doc.landing_url,
  });
};
